import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClearRefinements, useHits, useInstantSearch, useRefinementList } from 'react-instantsearch';
import { type RepubIs } from '@cx-web/utils';
import clsx from 'clsx';
import { type RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';

import ScrollableRow from '~/components/ScrollableRow';
import { type BetInsightsConfig } from '~/configuration/BetInsightsConfiguration';
import { type BettingInsightsContent, type EsIndex } from '~/configuration/types';
import { withLock } from '~/hocs/withLock';
import useMedia from '~/providers/use-media';
import { Pill } from '~/vendor/cxy/components/Pill';

import { trackCategoryRefinementClick } from '../../tracking';

import styles from './styles/CategoriesRefinement.module.css';

export function CategoriesRefinement<TIndex extends EsIndex, TContent extends BettingInsightsContent<TIndex>>({
  categories,
  hasResults,
  isDisabled = false,
  repubIs,
}: {
  categories: ReturnType<BetInsightsConfig<TIndex, TContent>['ui']['results']['categories']>;
  hasResults: boolean;
  isDisabled?: boolean;
  repubIs: RepubIs;
}) {
  const { t } = useTranslation();
  const allText = t('CX_PLUS_CHIPS_FILTER_ALL');

  const { items: hits } = useHits();
  const attribute = categories?.attribute ?? '';
  const { items } = useRefinementList({ attribute, limit: 25 });
  const { setIndexUiState } = useInstantSearch();
  const { canRefine: canRefineClear, refine: clearRefinement } = useClearRefinements({
    includedAttributes: [attribute],
  });
  const { isDesktop } = useMedia();

  const toggleCategory = useCallback(
    (category: string) => {
      trackCategoryRefinementClick({ selection: category });
      const isSelected = items.some(item => item.isRefined && item.value === category);
      const nextCategories = category === allText || isSelected ? [] : [category];

      setIndexUiState(uiState => ({
        ...uiState,
        page: undefined,
        refinementList: { ...uiState.refinementList, [attribute]: nextCategories },
      }));
    },
    [allText, attribute, items, setIndexUiState],
  );

  useEffect(() => {
    if (!hits.length && canRefineClear) {
      clearRefinement();
    }
  }, [clearRefinement, hits, canRefineClear]);

  if (!categories) return null;

  const { labels, order } = categories;

  const sortedCategories = sortItemsByOrder(items, order);

  return (
    <section className={styles.categories} data-testid="categories">
      <ScrollableRow className={styles.sportFilter} showArrows={!isDisabled} showFade={!(repubIs.GZ && !isDesktop)}>
        {sortedCategories.length > 1 && (
          <CategoryPill
            active={items.every(item => !item.isRefined)}
            disabled={isDisabled || (!hasResults && !items.length)}
            onClick={() => toggleCategory(allText)}
          >
            {allText}
          </CategoryPill>
        )}
        {sortedCategories.map(item => (
          <CategoryPill
            active={sortedCategories.length === 1 || item.isRefined}
            disabled={isDisabled}
            key={item.value}
            onClick={() => toggleCategory(item.value)}
          >
            {labels?.[item.value]?.displayName ?? item.label}
          </CategoryPill>
        ))}
      </ScrollableRow>
    </section>
  );
}

const CategoryPill = ({
  active,
  children,
  disabled,
  onClick,
}: {
  active: boolean;
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) => (
  <Pill
    active={active}
    className={clsx(styles.pill, { [styles.active]: active })}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </Pill>
);

function sortItemsByOrder(
  items: RefinementListItem[],
  order: {
    end?: string[];
    start: string[];
  },
) {
  // Split items into start, end and other categories
  const startItems = items.filter(item => order.start.includes(item.value));
  const endItems = items.filter(item => order.end?.includes(item.value));
  const otherItems = items.filter(item => !order.start.includes(item.value) && !order.end?.includes(item.value));

  // Sort start items by their position in order.start
  const sortedStartItems = [...startItems].sort((a, b) => {
    const aIndex = order.start.indexOf(a.value);
    const bIndex = order.start.indexOf(b.value);
    return aIndex - bIndex;
  });

  // Sort end items by their position in order.end
  const sortedEndItems = [...endItems].sort((a, b) => {
    const aIndex = (order.end ?? []).indexOf(a.value);
    const bIndex = (order.end ?? []).indexOf(b.value);
    return aIndex - bIndex;
  });

  // Sort other items alphabetically
  const sortedOtherItems = [...otherItems].sort((a, b) => a.value.localeCompare(b.value));

  // Combine all sorted arrays in order
  return [...sortedStartItems, ...sortedOtherItems, ...sortedEndItems];
}

export const LockableCategoriesRefinement = withLock(CategoriesRefinement, 'componentWall', 'categories');
